import { useCallback, useEffect, useState } from 'react';
import {
  Card, CardMedia, MobileStepper, Typography,
  Stack, Container, Grid,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import useEmblaCarousel from 'embla-carousel-react';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import { useIsMobile } from '@client/hooks/dom.hooks';
import { ArrowButton } from '@client/components/common/Media/ArrowButton';
import { radii } from '@client/theme/constants';
import { testimonials } from './common/testimonials';
import Fade from './embla-plugin-fade';

const useStyles = makeStyles()((theme) => ({
  root: {
    [theme.breakpoints.down('md')]: {
      aspectRatio: 1.37,
      borderRadius: 0,
      width: '100%',
      zIndex: 0,
      userSelect: 'none',
      backgroundColor: theme.palette.info.dark,

      cursor: 'grab',
      position: 'relative',

      '&:hover > button': {
        opacity: 0.8,

        '&:hover': {
          opacity: 1,
        },
      },
    },

    [theme.breakpoints.up('md')]: {
      borderRadius: theme.radii.large,
      backgroundColor: theme.palette.info.xlight,
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      height: theme.spacing(32),
      width: `calc(100% - ${theme.spacing(6)})`,
      marginLeft: `${theme.spacing(3)} !important`,
      marginRight: `${theme.spacing(3)} !important`,
      padding: theme.spacing(6, 3, 3, 3),
      overflow: 'visible',
    },
  },
  embla: {
    overflow: 'visible',
    height: '100%',
    position: 'relative',
  },
  emblaContainer: {
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
  },
  emblaSlide: {
    height: '100%',
    flex: '0 0 100%',
    minWidth: '0',
    position: 'relative',
  },
  stepperRoot: {
    left: theme.spacing(2),
    top: theme.spacing(4),
    position: 'absolute',
    zIndex: 1,
    padding: 0,

    [theme.breakpoints.up('md')]: {
      left: theme.spacing(3),
      top: 0,
    },
  },
  stepperDot: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.grey[50],
    backgroundColor: 'transparent',
    width: theme.spacing(1),
    height: theme.spacing(1),
    borderRadius: theme.spacing(1),

    '&:not(:last-of-type)': {
      marginRight: theme.spacing(1 / 2),
    },

    [theme.breakpoints.up('md')]: {
      borderColor: theme.palette.grey[500],
    },
  },
  stepperDotActive: {
    [theme.breakpoints.down('md')]: {
      borderColor: theme.palette.grey[50],
      backgroundColor: theme.palette.background.paper,
      boxShadow: theme.shadows[4],
    },

    [theme.breakpoints.up('md')]: {
      backgroundColor: theme.palette.grey[500],
      borderColor: theme.palette.grey[500],
    },
  },
  media: {
    zIndex: -1,

    [theme.breakpoints.down('md')]: {
      opacity: 0.6,
      height: '100%',
      width: '100%',
    },

    [theme.breakpoints.up('md')]: {
      aspectRatio: 1.53,
      height: 'auto',
      width: 550,
      position: 'relative',
      marginTop: theme.spacing(15 * -1),
      borderRadius: radii.large,
    },
  },
  quote: {
    position: 'absolute',
    top: theme.spacing(7),
    left: theme.spacing(2),
    right: theme.spacing(2),
  },
  desktopStack: {
    paddingTop: theme.spacing(4),
  },
}));

export const Testimonials = () => {
  const { classes } = useStyles();
  const isMobile = useIsMobile();

  const [imageIndex, setImageIndex] = useState(0);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    watchDrag: true,
    watchResize: true,
    watchSlides: false,
  }, [isMobile ? WheelGesturesPlugin() : Fade()]);

  const handlePrevButtonClick: React.MouseEventHandler<HTMLButtonElement> = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    if (emblaApi) {
      emblaApi.scrollPrev();
    }
  }, [emblaApi]);

  const handleNextButtonClick: React.MouseEventHandler<HTMLButtonElement> = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    if (emblaApi) {
      emblaApi.scrollNext();
    }
  }, [emblaApi]);

  useEffect(() => {
    if (emblaApi) {
      const onSelect = () => {
        setImageIndex(emblaApi.selectedScrollSnap());
      };

      onSelect();

      emblaApi.on('select', onSelect);
      emblaApi.on('reInit', onSelect);
    }
  }, [emblaApi, setImageIndex]);

  if (isMobile) {
    return (
      <Card className={classes.root}>
        <div
          ref={emblaRef}
          className={classes.embla}
        >
          <div className={classes.emblaContainer}>
            {testimonials.map((testimonial) => (
              <figure
                key={testimonial.name}
                className={classes.emblaSlide}
              >
                <CardMedia
                  alt={testimonial.alt}
                  className={classes.media}
                  component="img"
                  loading="eager"
                  src={testimonial.src}
                  aria-hidden={imageIndex !== 0}
                />

                <Stack
                  component="figcaption"
                  className={classes.quote}
                  spacing={2}
                >
                  <Typography
                    color="white"
                    variant="h6"
                  >
                    “
                    {testimonial.text}
                    ”
                  </Typography>

                  <Typography
                    color="white"
                    variant="subtitle2"
                  >
                    {testimonial.alt}
                  </Typography>
                </Stack>
              </figure>
            ))}
          </div>
        </div>

        <MobileStepper
          variant="dots"
          steps={5}
          position="static"
          activeStep={imageIndex}
          backButton={null}
          nextButton={null}
          classes={{
            root: classes.stepperRoot,
            dot: classes.stepperDot,
            dotActive: classes.stepperDotActive,
          }}
        />
      </Card>
    );
  }

  return (
    <Card className={classes.root}>
      <Container
        ref={emblaRef}
        className={classes.embla}
        maxWidth="md"
      >
        <div className={classes.emblaContainer}>
          {testimonials.map((testimonial) => (
            <figure
              className={classes.emblaSlide}
              key={testimonial.name}
            >
              <Grid
                container
                spacing={3}
                alignItems="flex-start"
              >
                <Grid
                  item
                  xs={12}
                  md={5}
                >
                  <Stack
                    component="figcaption"
                    spacing={2}
                    className={classes.desktopStack}
                  >
                    <Typography
                      variant="h6"
                    >
                      “
                      {testimonial.text}
                      ”
                    </Typography>

                    <Typography
                      variant="subtitle2"
                    >
                      {testimonial.alt}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid
                  item
                  md={7}
                >
                  <CardMedia
                    alt={testimonial.alt}
                    className={classes.media}
                    component="img"
                    loading="eager"
                    src={testimonial.src}
                    aria-hidden={imageIndex !== 0}
                  />
                </Grid>
              </Grid>
            </figure>
          ))}
        </div>

        <MobileStepper
          variant="dots"
          steps={5}
          position="static"
          activeStep={imageIndex}
          backButton={null}
          nextButton={null}
          classes={{
            root: classes.stepperRoot,
            dot: classes.stepperDot,
            dotActive: classes.stepperDotActive,
          }}
        />
      </Container>

      <ArrowButton
        visibility="visible"
        direction="left"
        onClick={handlePrevButtonClick}
        title="previous testimonial"
      />

      <ArrowButton
        visibility="visible"
        direction="right"
        onClick={handleNextButtonClick}
        title="next testimonial"
      />
    </Card>
  );
};
